import {
  eloquaHealthCheckOrganization,
  updateRegionInSalesforceOrganization,
  getCrmObjectTypesFieldMappings,
  getStandardFieldsFieldMappings,
  fetchCrmFieldsOrganization,
  fetchCrmFieldsV2Organization,
  listFieldMappings,
  CrmField,
  createFieldMappings,
  defaultMappingCreationOrganization,
  deleteFieldMappingsOrganization,
  updateSalesforceBaseUrlOrganization,
  fetchWistiaExistingProjectsOrgOrganization,
  getCampaignMemberStatusesOrgOrganization,
  validateSalesforceCampaignIdV2OrgOrganization,
  populateSalesforceCampaignDataOrganization,
  createMarketoGenericCustomActivityOrganization,
  listCrmSolutionInstance,
  createCrmSolutionInstance,
  updateStatusCrmSolutionInstance,
  destroyCrmSolutionInstance,
  createMarketoCustomActivityTypesOrganization,
  syncMarketoActivityTypeCrmSolutionInstance,
  listIntegrationsSetting,
  updateIntegrationSettingsAndOrganizationWithCommonFields,
  IntegrationsSetting,
  PatchedUpdateIntegrationSettingsRequest,
  defaultMappingCreationV2Organization,
  updateHubspotPortalIdOrganization,
  updateSalesforceOrgIdOrganization,
  generateCsrfTokenForTray,
  slackCurrentUserCrmSolutionInstance,
  sendSlackMessageCrmSolutionInstance
} from '@goldcast/api/backend';
import logger from '@goldcast/logger';

import analytics from '@/analytics';
import router from '@/router';
import {
  needANewTrayToken,
  getSegmentAnalyticsPayload,
  areThereMissingAuthentications,
  parseFieldMappingRequestBody,
  parseErrorFromServer,
  parseIntegrationApiError,
  sleep
} from '@/studio/orgIntegration/store/utils';
import { getEnv } from '@/utils/env';
import {
  create_tray_user,
  get_tray_user,
  login_to_tray,
  get_the_user_auths,
  get_all_the_solutions,
  generate_authorization_code
} from '@/utils/tray-graphql-apis.js';

const solutionPath = `${getEnv('VUE_APP_TRAY_APP_URL')}/external/solutions/${getEnv('VUE_APP_TRAY_PARTNER')}`;
const editAuthPath = `${getEnv('VUE_APP_TRAY_APP_URL')}/external/auth/edit/${getEnv('VUE_APP_TRAY_PARTNER')}`;

const marketoActivityTypesToSync = [
  {
    api_name: 'goldcastEngagementType',
    field: 'marketo_user_engagement_id',
    successToastMsg: 'Marketo "User Engagement" custom activity created.'
  },
  {
    api_name: 'goldcastUserProfile',
    field: 'marketo_user_profile_id',
    successToastMsg: 'Marketo "User Profile" custom activity created.'
  },
  {
    api_name: 'goldcastEngagementActivity',
    field: 'marketo_goldcast_generic_custom_activity_id',
    successToastMsg: 'Successfully created "Goldcast Engagement Activity" in your Marketo Account.'
  }
] as const;

export default {
  async createTrayUser(context) {
    const externalUserId = context.rootState.org.currentOrg.id;
    const name = context.rootState.org.currentOrg.name;
    let tray_id = '';
    try {
      const trayUserAuth = await context.dispatch('makeTrayApiCall', {
        body: {
          query: create_tray_user,
          variables: { externalUserId, name }
        }
      });
      tray_id = trayUserAuth?.data?.createExternalUser?.userId;
      const orgNewDetails = {
        id: externalUserId,
        tray_id
      };
      await context.dispatch(
        'org/updateOrgData',
        {
          input: orgNewDetails
        },
        {
          root: true
        }
      );
    } catch (e) {
      if (e === 'user_already_exists') {
        await context.dispatch('getTrayUser');
      } else {
        throw e;
      }
    } finally {
      const userId = context.rootState.org.currentOrg.tray_id;
      if (userId) {
        await context.dispatch('loginToTray', userId);
      }
    }
  },
  async getTrayUser(context) {
    const externalUserId = context.rootState.org.currentOrg.id;
    const tray_user = await context.dispatch('makeTrayApiCall', {
      body: {
        query: get_tray_user,
        variables: {
          userCriteria: {
            externalUserId: context.rootState.org.currentOrg.id
          }
        }
      }
    });
    const tray_id = tray_user.data.users.edges[0].node.id;
    const orgNewDetails = {
      id: externalUserId,
      tray_id
    };
    await context.dispatch(
      'org/updateOrgData',
      {
        input: orgNewDetails
      },
      {
        root: true
      }
    );
  },
  async loginToTray(context) {
    const userId = context.rootState.org.currentOrg.tray_id;
    if (needANewTrayToken(context.rootState.user.trayUserToken)) {
      const trayUserAuth = await context.dispatch('makeTrayApiCall', {
        body: {
          query: login_to_tray,
          variables: { userId }
        }
      });
      context.dispatch(
        'user/setTrayUserToken',
        {
          token: trayUserAuth?.data?.authorize?.accessToken ?? null,
          tokenCreatedAt: new Date().getTime()
        },
        {
          root: true
        }
      );
    }
  },
  async fetchLiveSolutions(context) {
    context.commit('setTrayLiveSolutionsLoading', true);
    const solutions = await context.dispatch('makeTrayApiCall', {
      body: {
        query: get_all_the_solutions,
        variables: {
          tags: [getEnv('VUE_APP_ENVIRONMENT')]
        }
      }
    });
    const liveSolutions = (solutions?.data?.viewer?.solutions?.edges ?? []).reduce((s, liveS) => {
      const customFieldsSet = {};
      (liveS.node.customFields ?? []).forEach(cF => {
        customFieldsSet[cF.key] = cF.value;
      });
      s[liveS.node.id] = {
        ...liveS.node,
        name: customFieldsSet['display_name'] || liveS.node.title,
        icon: customFieldsSet['icon']
      };
      return s;
    }, {});
    context.commit('setTrayLiveSolutions', liveSolutions);
    context.commit('setTrayLiveSolutionsLoading', false);
  },
  async fetchSolutionInstances(context, silently = false) {
    if (!silently) {
      context.commit('setSolutionInstancesLoading', true);
    }

    const userToken = context.rootState.user.trayUserToken?.token;
    let solutionInstances: any = [];
    solutionInstances = await listCrmSolutionInstance({
      headers: {
        'user-token': userToken
      },
      queryParams: {}
    });
    const solutionIs = solutionInstances?.data?.viewer?.solutionInstances?.edges ?? [];
    const modifiedSolutionIs = solutionIs.map(solutionI => {
      const customFieldsSet = {};
      (solutionI.node.solution.customFields ?? []).forEach(cF => {
        if (cF.key === 'auth_required') {
          customFieldsSet[cF.key] = (cF.value ?? '').split(',');
        } else {
          customFieldsSet[cF.key] = cF.value;
        }
      });

      const authIds = (solutionI.node.authValues ?? []).map(auth => auth.authId);

      const allExistingAuthDetails = context.getters.getAuthDetailsByIds(authIds);
      const allExistingAuthNames = allExistingAuthDetails.map(auth => auth.authTrayTitle);

      const solutionTitle = customFieldsSet['display_name'] || solutionI.node.solution.title;

      const newSolutionInstance = {
        name: solutionI.node.name,
        id: solutionI.node.id,
        solutionTitle,
        solutionId: solutionI.node.solution.id,
        description: solutionI.node.solution.description,
        icon: customFieldsSet['icon'] ?? '',
        enabled: solutionI.node.enabled,
        created: solutionI.node.created,
        authValues: solutionI.node.authValues,
        teams: solutionI.node.teams,
        default: solutionI.node.default,
        installedWorkspaces: solutionI.node.installed_workspaces,
        allExistingAuthDetails,
        missingAuths: areThereMissingAuthentications(
          customFieldsSet['auth_required'] ?? [],
          allExistingAuthNames,
          solutionI.node.configValues
        ),
        inHouseAuthenticationName: solutionI.node.authName,
        isNativeIntegration: solutionI.node.is_native_integration
      };

      const { currentSolutionInstance } = context.state;

      if (currentSolutionInstance && newSolutionInstance.id === currentSolutionInstance.id) {
        context.commit('setCurrentSolutionInstance', newSolutionInstance);
      }
      return newSolutionInstance;
    });
    context.commit('setTraySolutionInstances', modifiedSolutionIs);
    context.commit('setSolutionInstancesLoading', false);
  },
  async fetchAuthentications(context, silently = false) {
    if (!silently) {
      context.commit('setAuthenticationsLoading', true);
    }

    const userAuths = await context.dispatch('makeTrayUserApiCall', {
      query: get_the_user_auths
    });
    const auths = (userAuths?.data?.viewer?.authentications?.edges ?? []).map(auth => ({
      id: auth.node.id,
      name: auth.node.name,
      service: auth.node.service
    }));
    context.commit('setTrayAuthentications', auths);
    context.commit('setAuthenticationsLoading', false);
  },
  async editAuthenticationPath(context) {
    context.commit('setIsTrayLoading', true);
    context.commit('setTrayError', null);
    try {
      const orgTrayId = context.rootState.org.currentOrg.tray_id;
      const authId = context.state.currentAuthentication.id;
      const authorizationCode = await context.dispatch('generateUserAuthorizationCode', orgTrayId);
      const iframeSrc = `${editAuthPath}/${authId}?code=${authorizationCode}`;
      context.commit('setCurrentAuthenticationIframeSrc', iframeSrc);
    } catch (error) {
      context.commit('setCurrentAuthenticationIframeSrc', null);
      context.commit('setTrayError', error);
    } finally {
      context.commit('setIsTrayLoading', false);
    }
  },
  async editSolutionInstancePath(context) {
    context.commit('setIsTrayLoading', true);
    context.commit('setTrayError', null);
    try {
      const orgTrayId = context.rootState.org.currentOrg.tray_id;
      const solutionInstanceId = context.state.currentSolutionInstance.id;
      const authorizationCode = await context.dispatch('generateUserAuthorizationCode', orgTrayId);
      const iframeSrc = `${solutionPath}/configure/${solutionInstanceId}?code=${authorizationCode}`;
      context.commit('setCurrentSolutionIframeSrc', iframeSrc);
    } catch (error) {
      context.commit('setCurrentSolutionIframeSrc', null);
      context.commit('setTrayError', error);
    } finally {
      context.commit('setIsTrayLoading', false);
    }
  },
  async createSolutionInstance(context, { solutionId, name }) {
    context.commit('setTrayError', null);
    try {
      const userToken = context.rootState.user.trayUserToken?.token;
      let newSolutionInstanceData: any = {};
      newSolutionInstanceData = await createCrmSolutionInstance({
        body: {
          instanceName: name,
          solutionId
        },
        headers: {
          'user-token': userToken
        }
      });
      const solutionI = newSolutionInstanceData?.data?.createSolutionInstance?.solutionInstance;

      const customFieldsSet = {};
      (solutionI.solution.customFields ?? []).forEach(cF => {
        if (cF.key === 'auth_required') {
          customFieldsSet[cF.key] = (cF.value ?? '').split(',');
        } else {
          customFieldsSet[cF.key] = cF.value;
        }
      });

      const authIds = (solutionI.authValues ?? []).map(auth => auth.authId);
      const allExistingAuthDetails = context.getters.getAuthDetailsByIds(authIds);
      const allExistingAuthNames = allExistingAuthDetails.map(auth => auth.authTrayTitle);

      const newSolutionInstance = {
        name: solutionI.name,
        id: solutionI.id,
        solutionTitle:
          customFieldsSet['display_name'] || solutionI?.node?.solution?.title || solutionI?.solution?.title,
        solutionId: solutionI.solution.id,
        description: solutionI.solution.description,
        icon: customFieldsSet['icon'] ?? '',
        enabled: solutionI.enabled,
        created: solutionI.created,
        default: solutionI.default,
        teams: solutionI.teams,
        installedWorkspaces: solutionI.installed_workspaces,
        authValues: solutionI.authValues,
        allExistingAuthDetails,
        missingAuths: areThereMissingAuthentications(
          customFieldsSet['auth_required'] ?? [],
          allExistingAuthNames,
          solutionI?.node?.configValues
        )
      };

      const segmentAnalyticsPayload = getSegmentAnalyticsPayload(context, {
        solutionId: newSolutionInstance.solutionId,
        solutionName: newSolutionInstance.solutionTitle,
        solutionInstanceId: newSolutionInstance.id,
        solutionInstanceName: newSolutionInstance.name,
        action: 'created'
      });
      analytics.track('tray_solution_instance', segmentAnalyticsPayload);

      context.commit('setTraySolutionInstances', [...context.state.traySolutionInstances, newSolutionInstance]);

      return newSolutionInstance;
    } catch (error) {
      context.commit('setTrayError', error);
    }
  },
  async updateSolutionInstanceStatus(context, { solutionInstanceId, enabled }) {
    context.commit('setTrayError', null);
    try {
      const userToken = context.rootState.user.trayUserToken?.token;
      let updatedSolutionInstanceData: any = {};
      updatedSolutionInstanceData = await updateStatusCrmSolutionInstance({
        body: { enabled },
        solution_instance_id: solutionInstanceId,
        headers: {
          'user-token': userToken
        }
      });
      //TODO: Remove this when tray fix the issue where updateSolutionStatus is taking time to reflect the changes on their db or reader instance and they asked us to put a delay
      if (enabled) {
        await new Promise(resolve => setTimeout(resolve, 4000));
      }

      const updatedSolutionInstance = updatedSolutionInstanceData?.data?.updateSolutionInstance?.solutionInstance;

      const updatedTraySolutionInstancesList = context.state.traySolutionInstances?.map(solutionInstance => {
        if (solutionInstance.id === solutionInstanceId) {
          const updateCurrentSolutionInstance = {
            ...solutionInstance,
            enabled: updatedSolutionInstance.enabled
          };
          context.commit('setCurrentSolutionInstance', updateCurrentSolutionInstance);
          return updateCurrentSolutionInstance;
        }
        return solutionInstance;
      });
      context.commit('setTraySolutionInstances', updatedTraySolutionInstancesList);

      const segmentAnalyticsPayload = getSegmentAnalyticsPayload(context, {
        solutionInstanceId,
        action: enabled ? 'enabled' : 'disabled'
      });
      analytics.track('tray_solution_instance', segmentAnalyticsPayload);
    } catch (error) {
      context.commit('setTrayError', error);
    }
  },
  async deleteSolutionInstance(context, solutionInstanceId) {
    context.commit('setTrayError', null);
    try {
      const userToken = context.rootState.user.trayUserToken?.token;
      let deletedSolutionInstanceData: any = {};
      deletedSolutionInstanceData = await destroyCrmSolutionInstance({
        solution_instance_id: solutionInstanceId,
        headers: {
          'user-token': userToken
        }
      });

      const segmentAnalyticsPayload = getSegmentAnalyticsPayload(context, {
        solutionInstanceId,
        action: 'deleted'
      });
      analytics.track('tray_solution_instance', segmentAnalyticsPayload);

      const deletedSolutionInstance = deletedSolutionInstanceData?.data?.removeSolutionInstance?.clientMutationId;
      const existingSolutionIs = (context.state.traySolutionInstances ?? []).filter(
        solutionI => solutionI.id !== solutionInstanceId
      );
      context.commit('setTraySolutionInstances', existingSolutionIs);
      return {
        solutionInstanceId,
        clientMutationId: deletedSolutionInstance
      };
    } catch (error: any) {
      context.commit('setTrayError', error);
      throw error;
    }
  },
  sendAnalyticsDataToSegmentOnFinish(context) {
    const segmentAnalyticsPayload = getSegmentAnalyticsPayload(context, {
      action: 'enabled'
    });
    analytics.track('tray_solution_instance', segmentAnalyticsPayload);
  },
  async generateUserAuthorizationCode(context, userId) {
    const trayUserAuthCode = await context.dispatch('makeTrayApiCall', {
      body: {
        query: generate_authorization_code,
        variables: { userId }
      }
    });
    return trayUserAuthCode?.data?.generateAuthorizationCode?.authorizationCode;
  },
  async makeTrayUserApiCall(context, body) {
    if (needANewTrayToken(context.rootState.user.trayUserToken)) {
      const orgTrayId = context.rootState.org.currentOrg.tray_id;
      if (orgTrayId) {
        await context.dispatch('loginToTray', orgTrayId);
      }
    }
    return context.dispatch('makeTrayApiCall', {
      body,
      usingUserToken: true
    });
  },
  async makeTrayApiCall(context, { body, usingUserToken = false }) {
    const res = await context.dispatch(
      'makeapicall',
      {
        method: 'POST',
        completeURL: usingUserToken ? getEnv('VUE_APP_TRAY_USER_ENDPOINT') : getEnv('VUE_APP_TRAY_MASTER_ENDPOINT'),
        body: JSON.stringify(body),
        ...(usingUserToken && {
          headers: {
            'tray-token': context.rootState.user.trayUserToken.token
          }
        }),
        useIdToken: true
      },
      {
        root: true
      }
    );
    if (res.errors) {
      const defaultErrorStatement = 'Something has gone wrong. Please Try Again!!';
      if (Array.isArray(res.errors)) {
        const firsterror = res.errors[0];
        if (firsterror) {
          const errorCode = firsterror.extensions.code;
          if (errorCode) {
            throw errorCode;
          }
          throw firsterror.message;
        } else {
          throw defaultErrorStatement;
        }
      }
      throw defaultErrorStatement;
    }
    return res;
  },
  async fetchSlackCurrentUser(context) {
    try {
      const solutionInstanceId = context.state.currentSolutionInstance?.id;
      if (!solutionInstanceId) {
        throw new Error('No current solution instance found');
      }

      const userToken = context.rootState.user.trayUserToken?.token;
      const res = await slackCurrentUserCrmSolutionInstance({
        solution_instance_id: solutionInstanceId,
        headers: {
          'user-token': userToken
        }
      });

      if (res?.response?.ok) {
        context.commit('setSlackCurrentUser', res.response);
      } else {
        logger.error('Failed to fetch Slack current user', res?.error);
        context.commit('setSlackCurrentUser', null);
      }

      return res;
    } catch (error) {
      logger.error('Failed to fetch Slack current user', error);
      context.commit('setSlackCurrentUser', null);
      return null;
    }
  },
  async sendSlackMessage(context, { channelId, message }: { channelId: string; message: string }) {
    try {
      const solutionInstanceId = context.state.currentSolutionInstance?.id;
      if (!solutionInstanceId) {
        throw new Error('No current solution instance found');
      }

      const userToken = context.rootState.user.trayUserToken?.token;
      const res = await sendSlackMessageCrmSolutionInstance({
        solution_instance_id: solutionInstanceId,
        body: {
          channel_id: channelId,
          message
        },
        headers: {
          'user-token': userToken
        }
      });

      if (!res?.response) {
        logger.error('Failed to send Slack message', res?.error);
      }

      return res;
    } catch (error) {
      logger.error('Failed to send Slack message', error);
    }
  },
  async createMarketoCustomActivityTypes({ commit, dispatch, rootState, rootGetters, state }) {
    commit('setTrayError', null);
    commit('setIsTrayLoading', true);
    try {
      const orgId = rootGetters['org/getCurrentOrgId'];
      const currentOrg = rootGetters['org/getCurrentOrg'];
      const useInhouseIntegrationAuth = currentOrg?.UseInHouseIntegrationAuth;
      const isNativeIntegration = state.currentSolutionInstance?.isNativeIntegration;
      const solutionInstanceId = state.currentSolutionInstance?.id;

      if (useInhouseIntegrationAuth && isNativeIntegration && solutionInstanceId) {
        const currentIntegrationSetting = state.integrationSettings?.find(
          (s: IntegrationsSetting) => s.entity_id === solutionInstanceId
        );
        if (!currentIntegrationSetting) {
          dispatch(
            'studioToast/triggerToast',
            {
              msg: 'Integration setting not found for the solution instance.',
              mode: 'error'
            },
            { root: true }
          );
          return;
        }

        const updatedFields: Record<string, number> = {};
        const syncErrors: string[] = [];

        for (const activityTypeToSync of marketoActivityTypesToSync) {
          try {
            const result = await syncMarketoActivityTypeCrmSolutionInstance({
              solution_instance_id: solutionInstanceId,
              body: { api_name: activityTypeToSync.api_name }
            });
            const activityTypeId = result?.response?.activity_type?.id;
            if (activityTypeId) {
              updatedFields[activityTypeToSync.field] = activityTypeId;
              dispatch(
                'studioToast/triggerToast',
                {
                  msg: activityTypeToSync.successToastMsg,
                  mode: 'success'
                },
                { root: true }
              );
            } else {
              syncErrors.push(activityTypeToSync.api_name);
            }
          } catch (error: any) {
            logger.error(`Failed to create Marketo custom activity type. Please try again.`, error?.message);
            dispatch(
              'studioToast/triggerToast',
              {
                msg: JSON.parse(error?.message)?.error?.message,
                mode: 'error'
              },
              { root: true }
            );
            syncErrors.push(activityTypeToSync.api_name);
          }
        }

        if (syncErrors.length > 0) {
          dispatch(
            'studioToast/triggerToast',
            {
              msg: `Failed to create Marketo custom activity types. Please try again.`,
              mode: 'error'
            },
            { root: true }
          );
          logger.error(`Failed to create Marketo custom activity types. Please try again.`, syncErrors);
          return;
        }

        const updatedIntegrationSettings: IntegrationsSetting =
          await updateIntegrationSettingsAndOrganizationWithCommonFields({
            id: currentIntegrationSetting.id,
            body: updatedFields as unknown as PatchedUpdateIntegrationSettingsRequest
          });
        commit('updateIntegrationSettingsItem', updatedIntegrationSettings);
        return;
      }

      let res = {} as any;
      res = await createMarketoCustomActivityTypesOrganization({
        id: orgId,
        body: {
          solution_instance_id: solutionInstanceId
        },
        headers: {
          'Content-Type': 'application/json',
          'user-token': rootState.user.trayUserToken.token
        }
      });
      if (res.goldcast_event) {
        dispatch(
          'studioToast/triggerToast',
          {
            msg: 'Marketo custom activity created.',
            mode: 'success'
          },
          { root: true }
        );
        return;
      }
      if (res.user_profile) {
        dispatch(
          'studioToast/triggerToast',
          {
            msg: 'Marketo "User Profile" custom activity created.',
            mode: 'success'
          },
          { root: true }
        );
      }
      if (res.user_engagement) {
        dispatch(
          'studioToast/triggerToast',
          {
            msg: 'Marketo "User Engagement" custom activity created.',
            mode: 'success'
          },
          { root: true }
        );
      }
      const marketoCustomActivityIds = {
        marketo_goldcast_event_custom_activity_id: res.goldcast_event,
        marketo_user_profile_id: res.user_profile,
        marketo_user_engagement_id: res.user_engagement
      };
      const currentSettings = state.integrationSettings?.find(
        (setting: IntegrationsSetting) => setting.entity_id === solutionInstanceId
      );
      const updatedIntegrationSettings = {
        ...currentSettings,
        ...marketoCustomActivityIds
      };
      commit('updateIntegrationSettingsItem', updatedIntegrationSettings);

      if (!res.user_profile || !res.user_engagement) {
        throw new Error(JSON.stringify(res));
      }
    } catch (error: any) {
      try {
        const res = JSON.parse(error.message);
        if (!res.user_profile) {
          dispatch(
            'studioToast/triggerToast',
            {
              msg: 'Failed to create "User Profile" custom activity in your Marketo!',
              duration: 10000,
              mode: 'error'
            },
            { root: true }
          );
        }
        if (!res.user_engagement) {
          dispatch(
            'studioToast/triggerToast',
            {
              msg: 'Failed to create "User Engagement" custom activity in your Marketo!',
              duration: 10000,
              mode: 'error'
            },
            { root: true }
          );
        }
      } catch {
        dispatch(
          'studioToast/triggerToast',
          {
            msg: 'Creating Marketo custom activity failed!',
            mode: 'error'
          },
          { root: true }
        );
      } finally {
        commit('setTrayError', error);
      }
    } finally {
      commit('setIsTrayLoading', false);
    }
  },
  async createMarketoGenericCustomActivity({ commit, dispatch, rootState, rootGetters, state }) {
    commit('setIsTrayLoading', true);
    try {
      const org = rootGetters['org/getCurrentOrg'];
      const userToken = rootState.user.trayUserToken;
      const solutionInstanceId = state.currentSolutionInstance?.id;
      const res: any = await createMarketoGenericCustomActivityOrganization({
        id: org.id,
        headers: { 'user-token': userToken.token },
        body: {
          solution_instance_id: solutionInstanceId
        }
      });
      const goldcastCustomActivityId = res?.goldcast_custom_activity;
      if (goldcastCustomActivityId) {
        dispatch(
          'studioToast/triggerToast',
          {
            msg: 'Successfully created "Goldcast Engagement Activity" in your Marketo Account.',
            mode: 'success'
          },
          { root: true }
        );
      }
      const updatedIntegrationSettings = {
        ...state.integrationSettings?.find((setting: IntegrationsSetting) => setting.entity_id === solutionInstanceId),
        marketo_goldcast_generic_custom_activity_id: goldcastCustomActivityId
      };
      commit('updateIntegrationSettingsItem', updatedIntegrationSettings);
    } catch (error: any) {
      dispatch(
        'studioToast/triggerToast',
        {
          msg: 'Failed to create "Goldcast Engagement Activity" in your Marketo Account.',
          mode: 'error'
        },
        { root: true }
      );
      logger.error('[orgIntegration/actions] createMarketoGenericCustomActivity failed', error);
    } finally {
      commit('setIsTrayLoading', false);
    }
  },
  async updateMarketoCSRFToken(context) {
    try {
      const orgId = context.rootGetters['org/getCurrentOrgId'];
      await generateCsrfTokenForTray({
        id: orgId
      });
    } catch (error: any) {
      logger.error('[orgIntegration/actions] updateMarketoCSRFToken failed', error);
    }
  },
  async updateHubspotPortalId(context) {
    context.commit('setTrayError', null);
    context.commit('setIsTrayLoading', true);

    try {
      const currentOrg = context.rootGetters['org/getCurrentOrg'];
      const userToken = context.rootState.user.trayUserToken;
      const solutionInstanceId = context.state.currentSolutionInstance?.id;
      await updateHubspotPortalIdOrganization({
        id: currentOrg?.id,
        body: { solution_instance_id: solutionInstanceId },
        headers: { 'user-token': userToken.token }
      });

      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'HubSpot Portal ID updated successfully.',
          mode: 'success'
        },
        { root: true }
      );
    } catch (error) {
      context.commit('setTrayError', error);
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'HubSpot portal ID update failed!',
          mode: 'error'
        },
        { root: true }
      );
    } finally {
      context.commit('setIsTrayLoading', false);
    }
  },
  async updateSalesforceOrgId(context) {
    context.commit('setTrayError', null);
    context.commit('setIsTrayLoading', true);

    const currentOrg = context.rootGetters['org/getCurrentOrg'];
    const userToken = context.rootState.user.trayUserToken;
    const solutionInstanceId = context.state.currentSolutionInstance?.id;
    return new Promise((resolve, reject) => {
      updateSalesforceOrgIdOrganization({
        id: currentOrg?.id,
        body: { solution_instance_id: solutionInstanceId },
        headers: { 'user-token': userToken.token }
      })
        .then(resolve)
        .catch(error => {
          context.commit('setTrayError', error);
          reject(error);
        })
        .finally(() => {
          context.commit('setIsTrayLoading', false);
        });
    });
  },
  async updateSalesforceRegion(context) {
    const orgId = context.rootGetters['org/getCurrentOrgId'];
    const userToken = context.rootState.user.trayUserToken;
    try {
      await updateRegionInSalesforceOrganization({
        id: orgId,
        headers: { 'user-token': userToken.token }
      });

      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'Region updated successfully.',
          mode: 'success'
        },
        { root: true }
      );
    } catch (error: any) {
      const errorMessage = JSON.parse(error)?.error?.message;
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: errorMessage,
          mode: 'error'
        },
        { root: true }
      );
      logger.error('[orgIntegration] failed to update Salesforce region', error);
    }
  },
  async initializeIntegration(context) {
    if (!context.state.isInitializeIntegrationLoading) {
      return;
    }

    context.commit('setInitializeIntegrationLoading', true);
    const isTrayAccountActivated = context.rootGetters['org/isTrayAccountActivated'];
    try {
      await context.dispatch('fetchLiveSolutions');
      if (isTrayAccountActivated) {
        await context.dispatch('loginToTray');
        await context.dispatch('fetchAuthentications');
        await context.dispatch('fetchSolutionInstances');
        await context.dispatch('fetchIntegrationSettings');
      }
    } catch {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'Failed to initialize integrations! Try refreshing the page.',
          mode: 'error'
        },
        { root: true }
      );
      //This will only work when we install the solution and tray api throws error
      if (router?.currentRoute?.value?.query?.solutionID) {
        router.push({
          name: 'Applications'
        });
      }
    } finally {
      context.commit('setInitializeIntegrationLoading', false);
    }
  },
  async updateSalesforceBaseUrl(context) {
    const orgId = context.rootGetters['org/getCurrentOrgId'];
    const userToken = context.rootState.user.trayUserToken;
    try {
      // ! TODO - Amit to fix this
      await updateSalesforceBaseUrlOrganization({
        id: orgId,
        headers: { 'user-token': userToken.token }
      } as any);

      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'Your Salesforce Domain URL has been successfully updated.',
          mode: 'success'
        },
        { root: true }
      );
    } catch (error: any) {
      const errorMessage = JSON.parse(error)?.error?.message;
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: errorMessage,
          mode: 'error'
        },
        { root: true }
      );
      logger.error('[orgIntegration] failed to update Salesforce base URL', error);
    }
  },

  // Field Mapping *********************************************************
  async fetchCrmObjectTypes(context) {
    context.commit('setCrmObjectTypesLoading', true);
    const crmType = context.state.currentCrmType;
    const isEventMapping = context.rootGetters['eventIntegration/isEventMapping'];
    const isTemplateInstance = context.rootGetters['studioTemplateList/isTemplateInstance'];
    const isFormMapping = context.rootGetters['studioForms/getIsFormMapping'];
    try {
      const crmObjectTypes = await getCrmObjectTypesFieldMappings({ queryParams: { crm_type: crmType } });
      context.commit('setCrmObjectTypes', crmObjectTypes);
      context.commit('initializeFieldMappingStore');
      if (isEventMapping) {
        context.commit('eventIntegration/initializeEventFieldMappingStore', crmObjectTypes, { root: true });
      } else if (isTemplateInstance) {
        context.commit('studioTemplateList/initializeTemplateFieldMappingStore', crmObjectTypes, { root: true });
      } else if (isFormMapping) {
        context.commit('studioForms/initializeFormFieldMappingStore', crmObjectTypes, { root: true });
      }
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: `Failed to fetch CRM Object types for ${crmType}`,
          mode: 'error'
        },
        { root: true }
      );
      return { error };
    } finally {
      context.commit('setCrmObjectTypesLoading', false);
    }
  },
  async fetchFieldMappingSourceFields(context) {
    if (context.state.sourceFields.length !== 0) {
      return;
    }
    context.commit('setSourceFieldLoading', true);
    try {
      const sourceFields = await getStandardFieldsFieldMappings({});
      context.commit('setSourceFields', sourceFields);
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: `Failed to fetch Goldcast's source fields!`,
          mode: 'error'
        },
        { root: true }
      );
      return { error };
    } finally {
      context.commit('setSourceFieldLoading', false);
    }
  },
  async fetchFieldMappingTargetFields(context, { crmObjectType, force = false }) {
    if (!force && context.state.targetFields[crmObjectType].fields.length !== 0) {
      return;
    }
    const crmType = context.state.currentCrmType;

    context.commit('setTargetFieldLoading', {
      crmObjectType,
      isTargetFieldLoading: true
    });
    context.commit('setTargetFieldsError', {
      crmObjectType,
      targetFieldsError: null
    });

    try {
      const userToken = context.rootState.user.trayUserToken;
      const orgId = context.rootGetters['org/getCurrentOrgId'];
      const solutionInstanceId = context.state.currentSolutionInstance?.id;

      const maxRetries = 3;
      const baseDelayMs = 500;
      let lastError: any;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const targetFields = await fetchCrmFieldsOrganization({
            id: orgId,
            queryParams: {
              crm_type: crmType,
              crm_object_type: crmObjectType,
              solution_instance_id: solutionInstanceId
            },
            headers: { 'user-token': userToken.token }
          });

          if (crmType === context.state.currentCrmType) {
            context.commit('setTargetFields', { crmObjectType, targetFields });
          }
          return;
        } catch (err: any) {
          lastError = err;
          logger.warn(
            `fetchFieldMappingTargetFields attempt ${attempt} of ${maxRetries} failed for ${crmType}/${crmObjectType}`
          );
          if (attempt < maxRetries) {
            await sleep(baseDelayMs);
          }
        }
      }
      throw lastError || new Error('Failed to fetch target fields after retries');
    } catch (error: any) {
      const parsedError = parseIntegrationApiError(error);
      context.commit('setTargetFieldsError', {
        crmObjectType,
        targetFieldsError: parsedError
      });
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: `Failed to fetch ${crmType}'s ${crmObjectType} target fields!`,
          mode: 'error'
        },
        { root: true }
      );
      return { error };
    } finally {
      if (crmType === context.state.currentCrmType) {
        context.commit('setTargetFieldLoading', {
          crmObjectType,
          isTargetFieldLoading: false
        });
      }
    }
  },
  async fetchFieldMappingTargetFieldsV2(context, { crmObjectType, force = false }) {
    if (!force && context.state.targetFields[crmObjectType].fields.length !== 0) {
      return;
    }
    const crmType = context.state.currentCrmType;

    context.commit('setTargetFieldLoading', {
      crmObjectType,
      isTargetFieldLoading: true
    });
    context.commit('setTargetFieldsError', {
      crmObjectType,
      targetFieldsError: null
    });

    try {
      const userToken = context.rootState.user.trayUserToken;
      const orgId = context.rootGetters['org/getCurrentOrgId'];
      const solutionInstanceId = context.state.currentSolutionInstance?.id;
      const { response } = await fetchCrmFieldsV2Organization({
        id: orgId,
        queryParams: {
          crm_type: crmType,
          crm_object_type: crmObjectType,
          solution_instance_id: solutionInstanceId
        },
        headers: { 'user-token': userToken.token }
      });
      if (crmType === context.state.currentCrmType) {
        context.commit('setTargetFields', {
          crmObjectType,
          targetFields: (response as Record<string, any>).custom_fields
        });
      }
    } catch (error) {
      const parsedError = parseIntegrationApiError(error);
      context.commit('setTargetFieldsError', {
        crmObjectType,
        targetFieldsError: parsedError
      });
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: parsedError.message,
          mode: 'error'
        },
        { root: true }
      );
      return { error };
    } finally {
      if (crmType === context.state.currentCrmType) {
        context.commit('setTargetFieldLoading', {
          crmObjectType,
          isTargetFieldLoading: false
        });
      }
    }
  },
  async fetchFieldMappings(context, { crmObjectType, force = false }) {
    if (!force && context.state.fieldMappings[crmObjectType].mappings.length !== 0) {
      return;
    }

    context.commit('setFieldMappingLoading', {
      crmObjectType,
      isFieldMappingLoading: true
    });

    const crmType = context.state.currentCrmType;
    try {
      const solutionInstanceId = context.state.currentSolutionInstance?.id;
      let allFieldMappings = (await listFieldMappings({
        queryParams: {
          crm_type: crmType,
          crm_object_type: crmObjectType,
          entity_type: 'SOLUTION_INSTANCE',
          entity_id: solutionInstanceId
        }
      })) as CrmField[];
      allFieldMappings = allFieldMappings.sort((a, b) => (a.is_default === b.is_default ? 0 : a.is_default ? -1 : 1));

      const fieldMappings = allFieldMappings.filter(field => !field.is_custom_field);
      const customFieldMappings = allFieldMappings.filter(field => field.is_custom_field);

      context.commit('setFieldMappings', { crmObjectType, fieldMappings });
      context.commit('setCustomFieldMappings', { crmObjectType, customFieldMappings });
      context.commit('setPrevFieldMappings', {
        crmObjectType,
        prevFieldMappings: fieldMappings
      });
      const defaultErrorArray = new Array(fieldMappings.length).fill(null);
      context.commit('setFieldMappingRowError', {
        crmObjectType,
        errorsArray: defaultErrorArray
      });
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: `Failed to fetch ${crmType}'s field mappings for ${crmObjectType}!`,
          mode: 'error'
        },
        { root: true }
      );
      return { error };
    } finally {
      context.commit('setFieldMappingLoading', {
        crmObjectType,
        isFieldMappingLoading: false
      });
    }
  },
  async saveBulkFieldMappings(context, crmObjectType) {
    context.commit('setSaveFieldMappingLoading', {
      crmObjectType,
      isSaveFieldMappingLoading: true
    });

    const crmType = context.state.currentCrmType;
    const fieldMappings = context.state.fieldMappings[crmObjectType].mappings;
    const userToken = context.rootState.user.trayUserToken;
    const solutionInstanceId = context.state.currentSolutionInstance?.id;
    try {
      await createFieldMappings({
        body: {
          crm_type: crmType,
          crm_object_type: crmObjectType,
          field_mappings: parseFieldMappingRequestBody(fieldMappings),
          entity_type: 'SOLUTION_INSTANCE',
          entity_id: solutionInstanceId
        },
        headers: { 'user-token': userToken.token }
      });
      context.commit('setPrevFieldMappings', {
        crmObjectType,
        prevFieldMappings: fieldMappings
      });
      context.dispatch(
        'studioToast/triggerToast',
        { msg: 'Field mapping created/updated successfully.', mode: 'success' },
        { root: true }
      );
    } catch (error) {
      const parsedError = parseErrorFromServer(error);

      if (parsedError && typeof parsedError === 'string') {
        context.dispatch('studioToast/triggerToast', { msg: parsedError, mode: 'error' }, { root: true });
      } else {
        context.dispatch(
          'studioToast/triggerToast',
          {
            msg: 'Failed to create/update field mappings! Try again.',
            mode: 'error'
          },
          { root: true }
        );
      }

      if (Array.isArray(parsedError)) {
        context.commit('setFieldMappingRowError', {
          crmObjectType,
          errorsArray: parsedError
        });
      }
    } finally {
      context.commit('setSaveFieldMappingLoading', {
        crmObjectType,
        isSaveFieldMappingLoading: false
      });
    }
  },
  async createDefaultFieldMappings(context) {
    context.commit('setTrayError', null);
    context.commit('setIsTrayLoading', true);

    const crmType = context.state.currentCrmType;
    const solutionInstanceId = context.state.currentSolutionInstance?.id;
    try {
      await defaultMappingCreationV2Organization({
        body: { crm_type: crmType, entity_id: solutionInstanceId, entity_type: 'SOLUTION_INSTANCE' }
      });
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: `Failed to create default field mappings for ${crmType}!`,
          mode: 'error'
        },
        { root: true }
      );
      context.commit('setTrayError', error);
    } finally {
      context.commit('setIsTrayLoading', false);
    }
  },
  async deleteFieldMappings(context) {
    context.commit('setFieldMappingLoading', { isFieldMappingLoading: true });

    const crmType = context.state.currentCrmType;
    const orgID = context.rootGetters['org/getCurrentOrgId'];
    const solutionInstanceId = context.state.currentSolutionInstance?.id;
    try {
      await deleteFieldMappingsOrganization({
        id: orgID,
        body: {
          crm_type: crmType,
          solution_instance_id: solutionInstanceId
        }
      });
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: `Failed to delete existing field mappings for ${crmType}!`,
          mode: 'error'
        },
        { root: true }
      );
      return { error };
    } finally {
      context.commit('setFieldMappingLoading', {
        isFieldMappingLoading: false
      });
    }
  },
  async getEloquaAuthHealthCheckStatus(context, isCalledAfterAuth = false) {
    context.commit('setIsEloquaAuthHealthApiLoading', true);
    context.commit('setIsValidEloquaAuthConnected', true);
    const connectedEloquaFormId = context.rootGetters['org/eloquaConnectedEventFormID'];

    try {
      const orgID = context.rootGetters['org/getCurrentOrgId'];
      const userToken = context.rootState.user.trayUserToken;
      const apiResponse = await eloquaHealthCheckOrganization({
        id: orgID,
        headers: { 'user-token': userToken.token }
      });
      if ((apiResponse?.response as Record<string, any>)?.status === 'up') {
        if (isCalledAfterAuth) {
          context.dispatch(
            'studioToast/triggerToast',
            {
              msg: 'Authentication Successful',
              mode: 'success'
            },
            { root: true }
          );
        }
        if (connectedEloquaFormId) {
          await context.dispatch('eventIntegration/validateEloquaEventFormID', connectedEloquaFormId, {
            root: true
          });
        }
      } else if ((apiResponse?.response as Record<string, any>)?.status === 'down') {
        context.commit('setIsValidEloquaAuthConnected', false);
        if (connectedEloquaFormId) {
          context.commit('eventIntegration/setIsValidEloquaFormConnected', false, { root: true });
        }
        if (isCalledAfterAuth) {
          context.dispatch(
            'studioToast/triggerToast',
            {
              msg: 'Authentication Failed! Please try again',
              mode: 'error'
            },
            { root: true }
          );
        }
      } else {
        throw new Error(JSON.stringify(apiResponse));
      }
    } catch (error: any) {
      const parsedError = parseIntegrationApiError(error);
      const errorMessage = parsedError.message.includes('Trigger webhook Url does not exist')
        ? 'Something went wrong. The system is temporarily unavailable. Please try again later.'
        : parsedError.message;
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: errorMessage,
          mode: 'error'
        },
        { root: true }
      );
      connectedEloquaFormId &&
        context.dispatch(
          'studioToast/triggerToast',
          {
            msg: 'Failed to validate Form ID and Field Mapping.Please try again.',
            mode: 'error'
          },
          { root: true }
        );
      logger.error('[orgIntegration/actions] getEloquaAuthHealthCheckStatus failed', error);
    } finally {
      context.commit('setIsEloquaAuthHealthApiLoading', false);
    }
  },
  async fetchWistiaExistingProjectsOrganization(context) {
    if (context.state.wistiaProjects.length > 0) {
      return;
    }
    context.commit('setIsWistiaProjectsLoading', true);
    const orgID = context.rootGetters['org/getCurrentOrgId'];
    const userToken = context.rootState.user.trayUserToken;

    try {
      const response = await fetchWistiaExistingProjectsOrgOrganization({
        id: orgID,
        headers: { 'user-token': userToken.token }
      });
      context.commit('setWistiaProjects', (response?.response as Record<string, any>)?.wistia_projects);
    } catch (error: any) {
      const parsedError = parseIntegrationApiError(error);
      logger.error('[orgIntegration/actions] fetchWistiaExistingProjectsOrganization failed', error);
      return parsedError.message;
    } finally {
      context.commit('setIsWistiaProjectsLoading', false);
    }
  },
  async validateSalesforceCampaignIDOrganization(context, campaignID: string) {
    context.commit('setIsValidateCampaignIdOrgLoading', true);
    try {
      const userToken = context.rootState.user.trayUserToken;
      const currentOrg = context.rootGetters['org/getCurrentOrg'];
      const solutionInstanceId = context.state.currentSolutionInstance?.id;
      const res = await validateSalesforceCampaignIdV2OrgOrganization({
        id: currentOrg?.id,
        queryParams: {
          campaign_id: campaignID,
          solution_instance_id: solutionInstanceId
        },
        headers: { 'user-token': userToken.token }
      });
      return res;
    } catch (error: any) {
      logger.error('[orgIntegration/actions] validateSalesforceCampaignIDOrganization failed', error);
      return JSON.parse(error);
    } finally {
      context.commit('setIsValidateCampaignIdOrgLoading', false);
    }
  },
  connectSalesforceCampaignOrganization(context, campaignID: string) {
    return new Promise((resolve, reject) => {
      context.commit('setConnectSalesforceCampaignOrgLoading', true);
      const userToken = context.rootState.user.trayUserToken;
      const currentOrg = context.rootGetters['org/getCurrentOrg'];
      const solutionInstanceId = context.state.currentSolutionInstance?.id;
      populateSalesforceCampaignDataOrganization({
        id: currentOrg?.id,
        body: {
          campaign_id: campaignID,
          solution_instance_id: solutionInstanceId
        },
        headers: { 'user-token': userToken.token }
      })
        .then(({ response }) => {
          resolve(response);
        })
        .catch((error: any) => {
          const parsedError = parseIntegrationApiError(error);
          logger.error('[orgIntegration/actions] connectSalesforceCampaignOrganization failed', error);
          reject(parsedError);
        })
        .finally(() => {
          context.commit('setConnectSalesforceCampaignOrgLoading', false);
        });
    });
  },
  async fetchSalesforceCampaignMemberStatusOrganization(context, campaignID: string) {
    context.commit('setIsCampaignStatusOrgLoading', true);
    try {
      const currentOrg = context.rootGetters['org/getCurrentOrg'];
      const solutionInstanceId = context.state.currentSolutionInstance?.id;
      const userToken = context.rootState.user.trayUserToken;
      const res = await getCampaignMemberStatusesOrgOrganization({
        id: currentOrg?.id,
        queryParams: {
          campaign_id: campaignID,
          solution_instance_id: solutionInstanceId
        },
        headers: { 'user-token': userToken.token }
      });
      return res;
    } catch (error: any) {
      const errorMessage = JSON.parse(error)?.error?.message;
      logger.error('[orgIntegration/actions] fetchSalesforceCampaignMemberStatusOrganization failed', error);
      return errorMessage;
    } finally {
      context.commit('setIsCampaignStatusOrgLoading', false);
    }
  },
  // Integration Settings *********************************************************
  async fetchIntegrationSettings(context) {
    try {
      context.commit('setIntegrationSettingsLoading', true);
      const orgId = context.rootGetters['org/getCurrentOrgId'];
      const res: any = await listIntegrationsSetting({
        queryParams: {
          organization: orgId,
          entity_type: 'SOLUTION_INSTANCE'
        } as any
      });
      context.commit('setIntegrationSettings', res);
    } catch (error: any) {
      logger.error('[orgIntegration/actions] fetchIntegrationSettings failed', error);
      throw error;
    } finally {
      context.commit('setIntegrationSettingsLoading', false);
    }
  },
  async updateIntegrationSettings(context, { input }: { input: PatchedUpdateIntegrationSettingsRequest }) {
    context.commit('setIntegrationSettingsLoading', true);
    try {
      const currentSolutionInstance = context.state?.currentSolutionInstance;
      const currentIntegrationSetting = context.state?.integrationSettings?.find(
        s => s.entity_id === currentSolutionInstance?.id
      );
      if (!currentIntegrationSetting) {
        throw new Error('Integration setting not found');
      }
      const updatedIntegrationSettings: IntegrationsSetting =
        await updateIntegrationSettingsAndOrganizationWithCommonFields({
          id: currentIntegrationSetting.id,
          body: input
        });
      context.commit('updateIntegrationSettingsItem', updatedIntegrationSettings);
    } catch (error: any) {
      logger.error('[orgIntegration/actions] updateIntegrationSettings failed', error);
      throw error;
    } finally {
      context.commit('setIntegrationSettingsLoading', false);
    }
  }
};
